import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import { UserCardTarget, useUserCard } from "../../contexts/userCard/context";

import styles from "./UserCardAvatarArea.module.css";

export interface UserCardAvatarAreaProps {
  /** Absent (or without id) leaves the area inert. */
  user?: UserCardTarget;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Wraps an avatar to open the user card on click.
 *
 * The press is swallowed natively rather than through React: surrounding
 * components (chat bubbles, players) bind their own handlers with
 * `addEventListener`, which run before React's delegated ones — and a React
 * `stopPropagation` would also cut the document-level outer-click those
 * components rely on to dismiss themselves.
 */
export const UserCardAvatarArea = React.memo(({
  user,
  children,
  className,
  style,
}: UserCardAvatarAreaProps) => {
  const userCard = useUserCard();
  const ref = useRef<HTMLDivElement>(null);

  const clickable = !!user?.id && !!userCard;
  const openRef = useRef<() => void>();
  openRef.current = user && userCard ? () => userCard.open(user) : undefined;

  useEffect(() => {
    const element = ref.current;
    if (!element || !clickable) return;
    const stop = (event: Event) => event.stopPropagation();
    const press = (event: Event) => {
      event.stopPropagation();
      openRef.current?.();
    };
    element.addEventListener("mousedown", stop);
    element.addEventListener("mouseup", stop);
    element.addEventListener("touchstart", stop);
    element.addEventListener("touchend", stop);
    element.addEventListener("click", press);
    return () => {
      element.removeEventListener("mousedown", stop);
      element.removeEventListener("mouseup", stop);
      element.removeEventListener("touchstart", stop);
      element.removeEventListener("touchend", stop);
      element.removeEventListener("click", press);
    };
  }, [clickable]);

  return (
    <div
      ref={ref}
      className={clsx(styles.area, { [styles.clickable]: clickable }, className)}
      style={style}
    >
      {children}
    </div>
  );
});
